import { BontonPanel } from './../../../../environments/bonton-panel';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgClass, DatePipe, CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, } from '@angular/forms';
import { themes } from 'app/utils/const';
import { Subject, debounceTime, distinctUntilChanged, filter, startWith, switchMap, takeUntil } from 'rxjs';
import { CacheService } from 'app/services/cache.service';
import { MatRadioModule } from '@angular/material/radio';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ToasterService } from 'app/services/toaster.service';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { LuxonDateAdapterService } from 'app/services/LuxonDateAdapter.service';
import { CommonUtils, MY_DATE_FORMATS } from 'app/utils/commonutils';
import { SettingsService } from 'app/services/settings.service';
import { AddTravellerModalComponent } from '../add-traveller-modal/add-traveller-modal.component';
import { InsuranceService } from 'app/services/insurance.service';
import { DurationChangeDirective } from 'app/common/duration-change.directive';
import { ValidationService } from 'app/services/validation.service';
import { VideoHelpService } from 'app/services/VideoHelp.service';

@Component({
    selector: 'app-insurance-main',
    templateUrl: './main.component.html',
    standalone: true,
    imports: [
        NgClass,
        DatePipe,
        FormsModule,
        ReactiveFormsModule,
        MatRadioModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        MatInputModule,
        MatSelectModule,
        MatDatepickerModule,
        MatDialogModule,
        NgxMatSelectSearchModule,
        SlickCarouselModule,
        CommonModule,
        DurationChangeDirective,
        AddTravellerModalComponent
    ],
    providers: [
        { provide: DateAdapter, useClass: LuxonDateAdapterService },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    ],
})
export class MainComponent implements OnInit {
    @ViewChild('travellerRef', { static: false }) travellerRef: ElementRef;
    @ViewChild('containerRef', { static: false }) containerRef: ElementRef;
    @ViewChild('paxdetails', { static: false }) paxDetails: ElementRef;
    @ViewChild(AddTravellerModalComponent) addTravellerModal: AddTravellerModalComponent;
    bontonPanel = BontonPanel;
    is_mobile = CommonUtils.isMobile;
    is_tablet = CommonUtils.isTablet;
    themes = themes;
    wlSettingsDetails: any = {};
    selectedField: string = '';
    isOpenTravellers: boolean = false;
    paxPopAbove: boolean = false;
    formGroup: FormGroup;
    countryList: any[] = [];
    travelDuration: number = 2;
    submitted: boolean = false;

    settings: any = {};
    todayDate = new Date();
    minDate: any = new Date(this.todayDate.setDate(this.todayDate.getDate() + 1));
    minDateReturn: any = new Date(this.todayDate.setDate(this.todayDate.getDate() + 2));
    insuranceData: any = {};
    recentInsuranceSearchList: any[] = [];

    _unsubscribeAll: Subject<any> = new Subject();
    isFirstService: boolean = CommonUtils.getB2COrderByUrl() === 'insurance';

    /**
     * Constructor
     * @param {Router} router - Service for navigation.
     * @param {FormBuilder} builder - Service for building reactive forms.
     * @param {InsuranceService} insuranceService - Service for insurance-related operations.
     * @param {MatDialog} matDialog - Service for opening dialogs.
     * @param {SettingsService} settingService - Service for managing application settings.
     * @param {CacheService} cacheService - Service for caching data.
     * @param {ToasterService} alertService - Service for displaying toast notifications.
     * @param {ChangeDetectorRef} cdr - Service for detecting and triggering change detection.
     * @param {ValidationService} validationService - Service for custom validation logic.
     */
    constructor(
        private router: Router,
        private builder: FormBuilder,
        private insuranceService: InsuranceService,
        private matDialog: MatDialog,
        private settingService: SettingsService,
        private cacheService: CacheService,
        private alertService: ToasterService,
        private cdr: ChangeDetectorRef,
        private validationService: ValidationService,
        private videoHelpService: VideoHelpService
    ) {
        this.settingService.settings.pipe(takeUntil(this._unsubscribeAll)).subscribe(settings => {
            this.settings = settings;
            this.wlSettingsDetails = settings;
            this.insuranceData = this.wlSettingsDetails?.service_search_sections?.find(x => x.section_for.toLowerCase() == "insurance");
            this.settingService.updateSeoPageMeta('insurance'); // Seo Page Meta Details Updates
        });
    }

    /**
     * Initializes the component.
     * Sets up the form group, subscribes to value changes for the destination filter,
     * and fetches recent insurance searches.
     * @returns {void}
     */
    ngOnInit(): void {
        this.formGroup = this.builder.group({
            country_code: [''],
            depart_date_time: [DateTime.fromJSDate(new Date()).plus({ days: 1 }).toFormat('yyyy-MM-dd')],
            return_date_time: [DateTime.fromJSDate(new Date()).plus({ days: 2 }).toFormat('yyyy-MM-dd')],
            duration: [2],
            age_list: [],
            destinationfilter: [''],
        });

        // calling get country-list api on value changes
        this.formGroup.get('destinationfilter').valueChanges.pipe(
            filter(search => !!search),
            startWith(''),
            debounceTime(400),
            distinctUntilChanged(),
            switchMap((searchValue) => {
                return this.insuranceService.getCountryCombo(searchValue)
            })
        ).subscribe({
            next: (res) => {
                this.countryList = res || [],
                    this.formGroup.get('country_code').patchValue(this.countryList[0])
            },
            error: err => this.alertService.showToast('error', err)
        });

        // valuchanges event to do dynamic date change based on duration
        this.onDurationChange();

        // recent search api call
        this.getRecentHotelSearch();
    }

    /**
     * Calculates the duration of travel in days.
     * @param {any} departureDate - The departure date.
     * @param {any} returnDate - The return date.
     * @returns {void}
     */
    calTravelDuration(departureDate: any, returnDate: any) {
        const departure = new Date(departureDate);
        departure.setHours(0, 0, 0, 0);
        const returnDateObj = new Date(returnDate);
        returnDateObj.setHours(0, 0, 0, 0);
        const diffInMilliseconds = returnDateObj.getTime() - departure.getTime();
        this.travelDuration = diffInMilliseconds / (1000 * 60 * 60 * 24) + 1;

        if (this.travelDuration < 0) {
            this.travelDuration = null;
        }

        // Update the form value without triggering valueChanges
        this.formGroup.get('duration').setValue(this.travelDuration, { emitEvent: false });
    }

    /**
     * Handles changes in the date and time form controls.
     * @param {any} val - The new date value.
     * @param {string} key - The form control key ('departDate' or 'returnDate').
     * @returns {void}
     */
    onDateTimeChange(val: any, key: string) {
        const transFormDate = DateTime.fromJSDate(new Date(val.value)).toFormat('yyyy-MM-dd');
        const { depart_date_time, return_date_time } = this.formGroup.value;
        let minDate = new Date(this.formGroup.get('depart_date_time').value);
        this.minDateReturn = DateTime.fromJSDate(new Date(minDate)).plus({ days: 1 }).toFormat('yyyy-MM-dd')

        const departure = new Date(depart_date_time);
        departure.setHours(0, 0, 0, 0);
        const returnDateObj = new Date(return_date_time);
        returnDateObj.setHours(0, 0, 0, 0);

        if (key == 'departDate') {
            if (departure >= returnDateObj) {
                let newReturnDate = DateTime.fromJSDate(new Date(this.minDateReturn)).toFormat('yyyy-MM-dd');
                this.formGroup.get('return_date_time').patchValue(newReturnDate);
            }
            this.formGroup.get('depart_date_time').patchValue(transFormDate)
        }
        else {
            this.formGroup.get('return_date_time').patchValue(transFormDate);
        }

        const departDate = this.formGroup.get('depart_date_time').value;
        const returnDate = this.formGroup.get('return_date_time').value;

        if (departDate && returnDate) {
            this.calTravelDuration(departDate, returnDate);
        } else {
            this.travelDuration = null;
        }
    }

    /**
     * Subscribes to changes in the 'duration' form control and updates the return date accordingly.
     * @returns {void}
     */
    onDurationChange() {
        this.formGroup.get('duration')?.valueChanges.subscribe(value => {
            const departDate = this.formGroup.get('depart_date_time').value;
            const returnDate = this.formGroup.get('return_date_time');
            if (value >= 2) {
                returnDate.patchValue(DateTime.fromJSDate(new Date(departDate)).plus({ days: value - 1 }).toFormat('yyyy-MM-dd'))
            }
        });
    }

    /**
     * Sets the default duration value if it is not already set.
     * @returns {void}
     */
    SetDefaultDate() {
        const inputControl = this.formGroup.get('duration');
        if (!inputControl.value) {
            inputControl.patchValue(2);
            this.cdr.detectChanges();
        }
    }

    /**
     * Toggle the guest selection dropdown open/closed and adjust its position for non-B2B views.
     * @returns void
     */
    openTraveller() {
        this.selectedField === 'travellers' ? this.selectedField = '' : this.selectedField = 'travellers';
        if (!this.bontonPanel.isB2B()) {
            setTimeout(() => {
                this.checkDropdownPosition()
            }, 0);
        }
    }

    /**
     * Calculate whether the rooms & guests dropdown should open above or below the field
     * based on available viewport space and the dropdown element height.
     * @returns void
     */
    checkDropdownPosition() {
        if (!this.containerRef || !this.paxDetails) {
            return;
        }
        // Get the position of the "Guest" field and the viewport
        const guestRect = this.containerRef.nativeElement.getBoundingClientRect();
        const dropdownHeight = this.paxDetails.nativeElement.offsetHeight;
        const viewportHeight = window.innerHeight;

        // Calculate the space available below the field
        const spaceBelow = viewportHeight - guestRect.bottom + 100;
        // Check if there's enough space below
        if (spaceBelow < dropdownHeight) {
            // Not enough space, so open above
            this.paxPopAbove = true;
        } else {
            // Enough space, open below
            this.paxPopAbove = false;
        }
    }

    /**
     * Fetches and processes recent insurance search records.
     * @returns {void}
     */
    getRecentHotelSearch() {
        this.insuranceService.getRecentSearchRecords().subscribe((res: any) => {
            this.recentInsuranceSearchList = res;

            for (let i in this.recentInsuranceSearchList) {
                let startDate = new Date(this.recentInsuranceSearchList[i].departDate);
                let endDate = new Date(this.recentInsuranceSearchList[i].returnDate);

                if (startDate && endDate) {
                    this.recentInsuranceSearchList[i].totalDays = this.insuranceService.getDateDiffrence(startDate, endDate) + 1;
                }
            }
        });
    }

    /**
     * Initiates a new search based on a recent search item.
     * @param {any} val - The recent search data.
     * @returns {void}
     */
    recentSearch(val: any): void {
        val.text = 'recentSearch';
        this.detail(val);
    }

    /**
     * Toggles the visibility of the traveller selection modal.
     * @param {any} [key] - Optional key to identify the trigger source.
     * @returns {void}
     */
    toggleTravellerModal(key?: any) {
        if (!this.isOpenTravellers) {
            this.openTravellerModal(false);
            if (key) {
                this.selectedField = 'travellers';
            }
        }
    }

    patchPaxData(event: any) {
        if (event && event.key === 'done') {
            let selectedAges = event.selectedAges.filter((item: any) => item !== null && item !== undefined && item !== "");
            this.formGroup.get('age_list').patchValue(selectedAges);
        }
        this.selectedField = '';
    }

    /**
     * Opens the modal for adding or editing traveller information.
     * @param {any} flag - A flag to indicate the modal's state or purpose.
     * @param {any} [ref] - Optional reference element for positioning.
     * @returns {void}
     */
    openTravellerModal(flag: any, ref?: any): void {
        this.isOpenTravellers = true;

        let rect: any = {}
        let dialogPosition = {}
        let dialogWidth = "";
        let obj = {}
        let targetRef = ref || this.travellerRef;

        if (!this.is_mobile() && targetRef?.nativeElement) {
            rect = targetRef.nativeElement.getBoundingClientRect();
            dialogPosition = {
                top: `${rect.bottom - 60}px`,
                left: `${rect.left}px`
            };

            if (this.bontonPanel.isB2B()) {
                dialogWidth = `${rect.width}px`
            } else {
                dialogWidth = `${rect.width + rect.width / 4}px`
            }

            obj = {
                data: { age_list: this.formGroup.get('age_list').value, submitted: flag },
                width: dialogWidth,
                position: dialogPosition,
                hasBackdrop: false,
                disableClose: true,
                enterAnimationDuration: '0ms',
                exitAnimationDuration: '0ms',
            }
        } else {
            obj = {
                data: { age_list: this.formGroup.get('age_list').value, submitted: flag },
                // height: '00px',
                // width: '600px',
                panelClass: 'full-screen-model',
                hasBackdrop: false,
                disableClose: true,
            }
        }

        this.matDialog
            .open(AddTravellerModalComponent, obj)
            .afterClosed()
            .subscribe({
                next: (res) => {
                    this.isOpenTravellers = false;
                    let selectedAges = res?.selectedAges?.filter((item: any) => item !== null && item !== undefined && item !== "");
                    if (res?.key == 'done') {
                        this.formGroup.get('age_list').patchValue(selectedAges)
                    }
                },
            });
    }

    /**
     * Processes and navigates to the insurance detail page with the search criteria.
     * @param {any} [val] - Optional data from a recent search or current form.
     * @returns {void}
     */
    detail(val?: any): void {
        let json = {};
        this.submitted = true;
        if (val && val?.text == 'recentSearch') {
            let contryObj = {
                description: val.destination,
                id: val.destination_id,
                is_domestic: val.is_domestic
            }
            let startDate = new Date(val.departDate);
            let endDate = new Date(val.returnDate);
            let totalDays = 2;

            if (startDate && endDate) {
                totalDays = this.insuranceService.getDateDiffrence(startDate, endDate) + 1;
            }

            let formGroup = {
                country_code: contryObj,
                depart_date_time: DateTime.fromJSDate(new Date(val.departDate)).toFormat('yyyy-MM-dd'),
                return_date_time: DateTime.fromJSDate(new Date(val.returnDate)).toFormat('yyyy-MM-dd'),
                duration: totalDays,
                age_list: val.age_list,
                destinationfilter: val.destination,
            };

            json['searchFormData'] = formGroup;
            json['searchFormData']['countryName'] = val.destination;
            json['searchFormData']['countryComboSearchValue'] = val.destination;

        } else {
            if (!this.formGroup.get('age_list').value?.length) {
                if (!this.isOpenTravellers) {
                    this.openTraveller()
                }
                return;
            }

            if (typeof this.formGroup.get('duration').value != 'number') {
                this.formGroup.get('duration').patchValue(parseInt(this.formGroup.get('duration').value))
            }

            if (!this.formGroup.get('duration').value) {
                this.formGroup.get('duration').patchValue(2)
            }

            let countryName = this.formGroup.get('country_code')?.value.description;
            json['searchFormData'] = this.formGroup.value;
            json['searchFormData']['countryName'] = countryName;

        }
        this.cacheService.MainFilterChanged(json, 'insurance');
        let searchCountryname = this.validationService.replaceSpaceToHyphen(json['searchFormData'].countryName);
        searchCountryname = this.validationService.removeSpecialCharacter(searchCountryname);

        let navigateURLData = '/insurance/detail/' + searchCountryname + '/' + json['searchFormData'].depart_date_time + '/' + json['searchFormData'].return_date_time;
        this.router.navigate([navigateURLData]);
    }

    /**
     * A comparison function for use with Angular's select components.
     * @param {any} v1 - The first value to compare.
     * @param {any} v2 - The second value to compare.
     * @returns {boolean} - True if the objects have the same 'id' property, false otherwise.
     */
    public compareWith(v1: any, v2: any) {
        return v1 && v2 && v1.id === v2.id;
    }

    /**
     * Cleans up subscriptions and closes any open dialogs when the component is destroyed.
     * @returns {void}
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        this.matDialog.closeAll()
    }

    helpVideo(sectionName: string) {
        this.videoHelpService.helpVideo(sectionName);
    }

}

